import React, { useCallback, useEffect, useMemo } from 'react';

import usePersistedState from 'hooks/usePersistedState';
import { AuthContext } from 'constants/contexts';
import { triggerAnalyticEvent } from 'utils/analytics';
import useFetchedData from 'hooks/useFetchedData';
import { useHistory } from 'react-router-dom';
import RedirectProvider from 'components/RedirectProvider';
import { EventEmitter } from 'utils/event';
import { EVENTS } from 'constants/events';
import { usePushNotifications } from './use-push-notifications';

function AuthProvider({ children }) {
  const [user, setUser] = usePersistedState(null, AuthContext.STORAGE_KEY);
  const isLoggedIn = Boolean(user?.token && user?.tokenVerified);
  const { data: fetchedUser } = useFetchedData(isLoggedIn && 'user/profile/', ({ message } = {}) => message);
  const { saveRedirectPath } = RedirectProvider.useRedirect();
  const history = useHistory();

  const redirectAfterLoggedIn = useCallback((path) => {
    path = path || history.location.pathname;
    if (isLoggedIn) {
      history.push(path);
    } else {
      setTimeout(() => {
        saveRedirectPath(path);
        history.push('/login');
      }, 10);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (fetchedUser) {
      setUser((u) => ({ ...u, ...fetchedUser }));
    }
  }, [fetchedUser, setUser]);

  usePushNotifications({ isLoggedIn });

  const contextValue = useMemo(() => ({
    user,
    logout() {
      setUser(null);
      EventEmitter.fire(EVENTS.SIGN_OUT);
      triggerAnalyticEvent('logout');
    },
    redirectAfterLoggedIn,
    setUser(u) { setUser((current) => ({ ...current, ...u })); },
    isLoggedIn,
  }), [user, setUser]);

  return (
    <AuthContext.Provider
      value={contextValue}
    >
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;
